/**
 * expense-printing — Impression notes de frais VisionPOS.
 *
 * Extrait de print-orchestrator.ts (Phase 3c — split modules > 500 LOC).
 * Copie stricte (4.A.16) — aucune modification de la logique métier.
 *
 * Contient :
 *   - printExpenseFromSale()  — note de frais depuis saleId (AIDL → Cloud → BT/Network)
 *
 * DETTE (Phase D) : les appels supabase.* doivent migrer vers src/api/printing/
 * (invariant 4.B.2). Le DEBT marker est sur l'import supabase direct.
 */

import { supabase } from '@/integrations/supabase/client'; // DEBT(Phase-D): legacy — frontière publique = src/api/printing/ // eslint-disable-line
import { devLog } from '@/lib/logger';
import { sendViaBluetooth } from './printer-adapters';
import { findPrinter, loadTicketConfig } from './printer-router';
import { tenantAddressLines } from './tenant-address';
import { printViaNetworkOrCloud } from './network-printer-adapter';

// ── printExpenseFromSale ─────────────────────────────────────────────────────

/**
 * Imprime une note de frais (justificatif salarié) depuis une vente existante.
 * AIDL intégrée → Cloud Printer → BT/Network.
 */
export async function printExpenseFromSale(
  tenantId: string,
  saleId: string,
  opts: {
    mealCount?: number;
    employeeName?: string;
    /** Sélection d'articles (split par consommation) — si fournie, la note ne
     *  contient QUE ces lignes et le TTC = leur somme (sinon toute la vente). */
    items?: { name: string; quantity: number; unitPrice: number; tvaRate: number }[];
    /** Sans détail : remplace les lignes d'articles par « Repas pour N
     *  personne(s) ». Les totaux HT/TVA/TTC restent TOUJOURS imprimés —
     *  un justificatif sans montant est inutilisable (spec user 2026-07-09). */
    hideAmounts?: boolean;
  } = {},
): Promise<boolean> {
  const mealCount = Math.max(1, opts.mealCount || 1);
  const employeeName = opts.employeeName || 'Employé';
  const hideAmounts = opts.hideAmounts === true;

  const [saleRes, itemsRes, tenantRes] = await Promise.all([
    supabase.from('sales').select('*').eq('id', saleId).maybeSingle(),
    supabase.from('sale_items').select('*').eq('sale_id', saleId),
    supabase.rpc('get_my_tenant_info'),
  ]);

  const sale = saleRes.data;
  if (!sale) throw new Error('Vente non trouvée');
  // Override sélection (split par consommation) → mappé sur la forme sale_items.
  const items = opts.items
    ? opts.items.map((it) => ({
        product_name: it.name,
        quantity: it.quantity,
        unit_price: it.unitPrice,
        tva_rate: it.tvaRate,
      }))
    : itemsRes.data || [];
  const tenant = Array.isArray(tenantRes.data) ? tenantRes.data[0] : tenantRes.data;
  const config = await loadTicketConfig('note_frais', tenantId);

  const tvaMap = new Map<number, { ht: number; tva: number; ttc: number }>();
  for (const it of items) {
    const rate = Number((it as { tva_rate?: number }).tva_rate || 0);
    const lineTtc = Number(it.unit_price) * Number(it.quantity);
    const lineHt = rate > 0 ? lineTtc / (1 + rate / 100) : lineTtc;
    const lineTva = lineTtc - lineHt;
    const cur = tvaMap.get(rate) || { ht: 0, tva: 0, ttc: 0 };
    cur.ht += lineHt;
    cur.tva += lineTva;
    cur.ttc += lineTtc;
    tvaMap.set(rate, cur);
  }
  const tvaBreakdown = Array.from(tvaMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([rate, v]) => ({ rate, ht: v.ht, tva: v.tva, ttc: v.ttc }));
  const totalHt = tvaBreakdown.reduce((s, t) => s + t.ht, 0);
  const totalTva = tvaBreakdown.reduce((s, t) => s + t.tva, 0);
  // Sélection (opts.items) → TTC = somme des lignes retenues ; sinon total vente complète.
  const totalTtc = opts.items ? tvaBreakdown.reduce((s, t) => s + t.ttc, 0) : Number(sale.total);
  const perPerson = totalTtc / mealCount;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge non typé
  const w = window as any;
  const b = w.SunmiPrinter || w.SunmiInnerPrinter;
  if (b) devLog('[printExpense] route candidate: AIDL');
  else if (w.SunmiPrinterX?.isCloudPrinterAvailable?.())
    devLog('[printExpense] route candidate: CloudPrinter');
  else devLog('[printExpense] AIDL+Cloud absents → fallback BT/Network');

  // ── AIDL intégrée (D3/D3Mini) ──
  if (b) {
    try {
      for (let i = 0; i < 5; i++) {
        try {
          if (b.isConnected?.()) break;
        } catch {
          /* ignore */
        }
        try {
          b.init?.();
        } catch {
          /* ignore */
        }
        await new Promise((r) => setTimeout(r, 400 + i * 200));
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
      const cpl = (config as any).largeur_papier === 58 ? 32 : 48;
      const ps = Math.max(
        14,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
        Math.min(22, (config as any).police_size ? (config as any).police_size - 2 : 18),
      );
      const sep = '-'.repeat(cpl);
      const dsep = '='.repeat(cpl);
      const padLine = (l: string, r: string) => {
        const maxLeft = cpl - r.length - 1;
        const trimmed = l.length > maxLeft ? l.substring(0, maxLeft) : l;
        return trimmed + ' '.repeat(Math.max(1, cpl - trimmed.length - r.length)) + r;
      };

      b.beginTransaction();
      b.setAlignment(1);
      b.setBold(true);
      b.setFontSize(ps + 4);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
      b.printText((config as any).header_title || tenant?.name || 'VisionPOS');
      b.setBold(false);
      b.setFontSize(ps);
      tenantAddressLines(tenant).forEach((line) => b.printText(line));
      if (tenant?.siret) b.printText('SIRET : ' + tenant.siret);
      b.printText(sep);
      b.setBold(true);
      b.setFontSize(ps + 4);
      b.printText('NOTE DE FRAIS');
      b.setBold(false);
      b.setFontSize(ps);
      if (sale.ticket_number) b.printText('Reference : ' + sale.ticket_number);
      b.printText(new Date(sale.created_at).toLocaleString('fr-FR'));
      b.printText('Salarie : ' + employeeName);
      b.printText('Couverts : ' + mealCount);
      b.printText(sep);
      b.setAlignment(0);
      if (hideAmounts) {
        // Sans détail : une seule ligne générique à la place des plats.
        b.printText(`Repas pour ${mealCount} personne${mealCount > 1 ? 's' : ''}`);
      } else {
        for (const item of items) {
          const qty = item.quantity;
          const name = qty > 1 ? `${qty}x ${item.product_name}` : item.product_name;
          b.printText(padLine(name, (qty * Number(item.unit_price)).toFixed(2) + ' EUR'));
        }
      }
      // Totaux HT/TVA/TTC : TOUJOURS imprimés, même « sans détail » —
      // c'est le justificatif (fix bug terrain WAVE 2026-07-09).
      b.printText(sep);
      b.printText(padLine('Total HT', totalHt.toFixed(2) + ' EUR'));
      for (const t of tvaBreakdown) {
        const lbl = t.rate > 0 ? `TVA ${t.rate}% (base ${t.ht.toFixed(2)})` : 'Exonéré';
        b.printText(padLine(lbl, t.tva.toFixed(2) + ' EUR'));
      }
      b.printText(padLine('Total TVA', totalTva.toFixed(2) + ' EUR'));
      b.printText(dsep);
      b.setBold(true);
      b.setFontSize(ps + 3);
      b.printText(padLine('TOTAL TTC', totalTtc.toFixed(2) + ' EUR'));
      b.setBold(false);
      b.setFontSize(ps);
      if (mealCount > 1) b.printText(padLine('Par personne', perPerson.toFixed(2) + ' EUR'));
      b.printText(dsep);
      b.setAlignment(1);
      b.setFontSize(ps - 2);
      b.printText('Justificatif note de frais');
      b.printText('TVA recuperable pour les professionnels');
      // Document non fiscal — jamais une preuve de paiement (le ticket NF525 l'est).
      b.setBold(true);
      b.printText('NON VALABLE POUR ENCAISSEMENT');
      b.setBold(false);
      b.feedLines(3);
      b.commitTransaction();
      return true;
    } catch (err) {
      console.error('[PrintService] AIDL expense error:', err);
    }
  }

  // ── Fallback Cloud Printer (D3 Pro) ──
  const px = w.SunmiPrinterX;
  if (px?.isCloudPrinterAvailable?.()) {
    try {
      await px.printReceiptExpense(
        JSON.stringify({
          store_name: tenant?.name || 'VisionPOS',
          store_siret: tenant?.siret || '',
          employee: employeeName,
          date: new Date(sale.created_at).toLocaleString('fr-FR'),
          ticket_number: sale.ticket_number || '',
          couverts: mealCount,
          // Sans détail : ligne générique unique côté payload + hide_amounts
          // forcé à false — les totaux s'impriment quel que soit le firmware.
          hide_amounts: false,
          items: hideAmounts
            ? [
                {
                  name: `Repas pour ${mealCount} personne${mealCount > 1 ? 's' : ''}`,
                  quantity: 1,
                  unit_price: totalTtc,
                },
              ]
            : items.map((i: { product_name: string; quantity: number; unit_price: number }) => ({
                name: i.product_name,
                quantity: i.quantity,
                unit_price: Number(i.unit_price),
              })),
          total_ht: totalHt,
          total_tva: totalTva,
          tva_breakdown: tvaBreakdown,
          total_ttc: totalTtc,
          per_person: perPerson,
        }),
      );
      return true;
    } catch (err) {
      console.error('[PrintService] Cloud expense error:', err);
    }
  }

  // ── Fallback BT/Network (FIX 2026-04-30) ──
  const printer = await findPrinter(tenantId, sale.register_id || undefined, 'receipt');
  if (printer) {
    const cpl = printer.char_per_line || 48;
    const sep = '-'.repeat(cpl);
    const dsep = '='.repeat(cpl);
    const padLine = (l: string, r: string) => {
      const maxLeft = cpl - r.length - 1;
      const trimmed = l.length > maxLeft ? l.substring(0, maxLeft) : l;
      return trimmed + ' '.repeat(Math.max(1, cpl - trimmed.length - r.length)) + r;
    };
    const ESC_INIT = '\x1B@';
    const ALIGN_C = '\x1B\x61\x01';
    const ALIGN_L = '\x1B\x61\x00';
    const BOLD_ON = '\x1B\x45\x01';
    const BOLD_OFF = '\x1B\x45\x00';
    const SIZE_L = '\x1D\x21\x10';
    const SIZE_N = '\x1D\x21\x00';
    const CUT = '\x1D\x56\x01';
    let t =
      ESC_INIT +
      ALIGN_C +
      BOLD_ON +
      SIZE_L +
      (tenant?.name || 'VisionPOS') +
      '\n' +
      SIZE_N +
      BOLD_OFF;
    tenantAddressLines(tenant).forEach((line) => {
      t += line + '\n';
    });
    if (tenant?.siret) t += 'SIRET : ' + tenant.siret + '\n';
    t += sep + '\n' + BOLD_ON + SIZE_L + 'NOTE DE FRAIS\n' + SIZE_N + BOLD_OFF;
    if (sale.ticket_number) t += 'Reference : ' + sale.ticket_number + '\n';
    t += new Date(sale.created_at).toLocaleString('fr-FR') + '\n';
    t += 'Salarie : ' + employeeName + '\n';
    t += 'Couverts : ' + mealCount + '\n';
    t += sep + '\n' + ALIGN_L;
    if (hideAmounts) {
      // Sans détail : une seule ligne générique à la place des plats.
      t += `Repas pour ${mealCount} personne${mealCount > 1 ? 's' : ''}\n`;
    } else {
      for (const item of items) {
        const qty = item.quantity;
        const name = qty > 1 ? `${qty}x ${item.product_name}` : item.product_name;
        t += padLine(name, (qty * Number(item.unit_price)).toFixed(2) + ' EUR') + '\n';
      }
    }
    // Totaux HT/TVA/TTC : TOUJOURS imprimés, même « sans détail » —
    // c'est le justificatif (fix bug terrain WAVE 2026-07-09).
    t += sep + '\n';
    t += padLine('Total HT', totalHt.toFixed(2) + ' EUR') + '\n';
    for (const tv of tvaBreakdown) {
      const lbl = tv.rate > 0 ? `TVA ${tv.rate}% (base ${tv.ht.toFixed(2)})` : 'Exonéré';
      t += padLine(lbl, tv.tva.toFixed(2) + ' EUR') + '\n';
    }
    t += padLine('Total TVA', totalTva.toFixed(2) + ' EUR') + '\n';
    t +=
      dsep +
      '\n' +
      BOLD_ON +
      SIZE_L +
      padLine('TOTAL TTC', totalTtc.toFixed(2) + ' EUR') +
      '\n' +
      SIZE_N +
      BOLD_OFF;
    if (mealCount > 1) t += padLine('Par personne', perPerson.toFixed(2) + ' EUR') + '\n';
    t +=
      dsep +
      '\n' +
      ALIGN_C +
      'Justificatif note de frais\nTVA recuperable pour les professionnels\n' +
      // Document non fiscal — jamais une preuve de paiement (le ticket NF525 l'est).
      BOLD_ON +
      'NON VALABLE POUR ENCAISSEMENT\n' +
      BOLD_OFF +
      '\n\n\n\n' +
      CUT;
    const bytes = new TextEncoder().encode(t);
    try {
      if (printer.connection_type === 'bluetooth' && printer.bt_mac_address) {
        const ok = await sendViaBluetooth(printer.bt_mac_address, bytes);
        if (ok) return true;
      }
      if (printer.connection_type === 'network' && printer.ip_address) {
        const ok = await printViaNetworkOrCloud(printer.ip_address, printer.port || 9100, bytes);
        if (ok) return true;
      }
    } catch (err) {
      console.error('[PrintService] BT/Network expense error:', err);
    }
  }

  console.warn('[PrintService] Aucune imprimante disponible pour note de frais');
  return false;
}
